import { Menu, MenuProps } from "antd";
import Sider from "antd/es/layout/Sider";
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DownArrow from "../../../assets/icons/sidebar/DownArrow";
import UpArrow from "../../../assets/icons/sidebar/UpArrow";
import MainLogo from "../../../assets/images/logo.svg";
import AllSidebarMenu from "../../../constants/AllSidebarMenu";
import { APP_NAME } from "../../../constants/constants";
import useModal from "../../../hooks/useModal";
import { GetEmployeeDetailsResponseType } from "../../../types";
import { stringToArray } from "../../../utils/array";
import Image from "../../ui/Image";
import TopUp from "./TopUp";
// import Small_LOGO from "/favicon.png";

type MenuItem = Required<MenuProps>["items"][number];

interface SidebarItemProps {
  collapsed: boolean;
  openMenuKeys: string[];
  handleOpenMenu: (keys: string[]) => void;
  isMobile?: boolean;
  data?: GetEmployeeDetailsResponseType;
}

export default function SidebarItems({
  collapsed,
  openMenuKeys,
  handleOpenMenu,
  isMobile,
  data,
}: SidebarItemProps) {
  const location = useLocation();
  const { open, handleOpen, handleClose } = useModal();
  const { authInfo } = useAppSelector((state) => state.auth);

  const isEmailVerified = data?.data?.employee?.member?.profile?.email_verified;

  function getItem(
    label: ReactNode,
    path: string,
    icon?: ReactNode,
    badge?: string,
    children?: MenuItem[],
    roles?: string[]
  ): MenuItem {
    return {
      key: pathString(path),
      icon,
      children,
      label: children?.length ? (
        label
      ) : label === "TopUp" ? (
        <span
          className="w-full block"
          onClick={() => handleOpen({ type: "VIEW" })}
        >
          {label}
        </span>
      ) : (
        <Link to={path}>
          {label}
          {badge && !collapsed ? (
            <button className="text-secondary text-xs font-medium uppercase px-2 py-0.5 bg-white rounded border border-secondary h-6 justify-center items-center inline-flex ml-1.5">
              beta
            </button>
          ) : undefined}
        </Link>
      ),
      roles,
    } as MenuItem;
  }

  // filter allowed menu
  const MenuItems: MenuItem[] = AllSidebarMenu.filter((el) =>
    el?.role?.find((rl) => rl === authInfo?.role)
  ).map(({ name, path, icon: Icon, children, badge }) =>
    getItem(
      name,
      path,
      Icon ? <Icon collapsed={collapsed} /> : undefined,
      badge,
      children?.length
        ? children
            .filter((el) => el?.role?.find((rl) => rl === authInfo?.role))
            .map(
              ({ name: cdName, path: cdPath, icon: CDIcon, badge: cdBadge }) =>
                getItem(
                  cdName,
                  cdPath,
                  CDIcon ? <CDIcon /> : undefined,
                  cdBadge
                )
            )
        : undefined
    )
  );

  // find active sub menu
  function getActiveMenu(data: any[]) {
    const strArr = stringToArray(location.pathname, "/");
    return data?.find((el) => el?.key === strArr[1])?.children ? true : false;
  }

  return (
    <div
      className={`sticky !bg-white z-50 ${
        !isEmailVerified && !isMobile
          ? "top-12 h-[calc(100vh-3rem)]"
          : "top-0 h-screen"
      }`}
    >
      {open?.type === "VIEW" ? (
        // <LoginOldSite open={open?.open} handleClose={handleClose} />
        <TopUp open={open?.open} handleClose={handleClose} />
      ) : null}
      <Sider trigger={null} collapsible collapsed={collapsed} width={312}>
        <div className={` bg-white`}>
          <div
            className={`flex h-[81px] items-center  ${
              collapsed ? "h-[4rem]" : "h-[6rem]"
            }`}
          >
            <div
              className={`w-full flex items-center ${
                collapsed ? "justify-center" : ""
              }`}
            >
              <Link
                className={`flex items-center ${
                  collapsed ? "justify-center" : "pl-3"
                }`}
                to="/"
              >
                <Image
                  width={collapsed ? 50 : 160}
                  src={collapsed ? "/favicon.svg" : MainLogo}
                  alt={APP_NAME}
                />
              </Link>
            </div>
          </div>
          <div
            className={`flex items-center bg-white px-4 pb-1.5 ${
              collapsed ? "gap-1" : "gap-4"
            }`}
          >
            <h2
              className={`text-display-medium text-xs font-semibold tracking-tight ${
                collapsed && "text-center"
              }`}
            >
              MENU
              {/* {!collapsed && "SOLUTION"} */}
            </h2>
            {<div className="flex-1 border-b border-display-light" />}
          </div>

          {/* sidebar item section */}
          <div
            className={`!overflow-auto bg-white sticky custom-scrollbar ${
              !isEmailVerified && !isMobile
                ? "h-[calc(100vh-9.3rem)]"
                : "h-[calc(100vh-6rem)] pt-1"
            }`}
          >
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[
                activePathString(
                  location.pathname,
                  "/",
                  getActiveMenu(MenuItems)
                ),
              ]}
              openKeys={openMenuKeys}
              onOpenChange={handleOpenMenu}
              items={MenuItems}
              className={`!bg-white !px-4 !select-none ${
                collapsed ? "px-3" : ""
              }`}
              rootClassName="custom-sidebar"
              expandIcon={({ isSubMenu, isOpen }) => {
                if (isSubMenu && !collapsed) {
                  return isOpen ? <UpArrow /> : <DownArrow />;
                }
              }}
            />
          </div>
        </div>
      </Sider>
    </div>
  );
}

const activePathString = (
  path: string,
  splitText: string,
  isSubMenu?: boolean
) => {
  const pathArray = stringToArray(path, splitText);
  return pathArray.slice(1, (isSubMenu ? 3 : 2) - 0).join("/");
};

const pathString = (path: string, splitText: string = "/") => {
  return stringToArray(path, splitText).slice(1, 3).join("/");
};
